// src/oura/ui/SearchBox.tsx
//
// A pill-shaped search field, 30px tall like the filter pills beside it, with
// a clear button once something is typed. Esc clears it too.

import { forwardRef } from "react";
import { SearchIcon, XIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { TYPE } from "./tokens";

export const SearchBox = forwardRef<HTMLInputElement, {
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    /** Read by a screen reader; the placeholder alone is not a label. */
    label: string;
    className?: string;
}>(function SearchBox({ value, onChange, placeholder, label, className }, ref) {
    return (
        <span className={cn("relative inline-flex h-[30px] items-center", className)}>
            <SearchIcon aria-hidden className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-gray-400" />
            <input
                ref={ref}
                type="search"
                value={value}
                aria-label={label}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Escape" && value) {
                        e.stopPropagation();
                        onChange("");
                    }
                }}
                className={cn(
                    TYPE.tc,
                    "h-full w-full appearance-none rounded-full bg-white pl-8 pr-8 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400",
                    "focus:outline-none focus:ring-2 focus:ring-blue-600 [&::-webkit-search-cancel-button]:hidden",
                )}
            />
            {value && (
                <button
                    type="button"
                    aria-label="Clear the search"
                    onClick={() => onChange("")}
                    className="absolute right-2 grid h-5 w-5 place-items-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                    <XIcon className="h-3.5 w-3.5" />
                </button>
            )}
        </span>
    );
});
